import { z } from "zod"
import { channelResponseSchema } from "../../campaigns/commonTypes"
import {
  PromptExperimentConfigurationSchema,
  PromptExperimentReasoningEffortEnum,
  PromptExperimentVerbosityEnum,
} from "./dtos"

export const PromptExperimentRunStatusEnum = ["Queued", "Running", "Completed", "Failed"] as const

export const BackofficePromptExperimentRunResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  batchId: z.string(),
  name: z.string(),
  promptName: z.string(),
  configuration: PromptExperimentConfigurationSchema,
  langsmithUrl: z.string().url().nullable(),
  status: z.enum(PromptExperimentRunStatusEnum),
  error: z.string().nullable(),
  createdAt: z.date().transform((value) => value.toISOString()),
  startedAt: z
    .date()
    .nullable()
    .transform((value) => (value ? value.toISOString() : null)),
  completedAt: z
    .date()
    .nullable()
    .transform((value) => (value ? value.toISOString() : null)),
})

export const BackofficePromptExperimentRunsResponseDataSchema = z.array(
  BackofficePromptExperimentRunResponseDataSchema,
)

export const BackofficePromptExperimentOptionsResponseDataSchema = z.object({
  prompts: z.array(
    z.object({
      promptName: z.string(),
      hubName: z.string(),
      model: z.string(),
      reasoningEffort: z.enum(PromptExperimentReasoningEffortEnum).nullable(),
      verbosity: z.enum(PromptExperimentVerbosityEnum).nullable(),
    }),
  ),
  models: z.array(
    z.object({
      model: z.string(),
      label: z.string(),
      reasoningEfforts: z.array(z.enum(PromptExperimentReasoningEffortEnum)),
      verbosities: z.array(z.enum(PromptExperimentVerbosityEnum)),
    }),
  ),
})

export const BackofficeConversationExperimentResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  experiment: z.string(),
  createdAt: z.date().transform((value) => value.toISOString()),
  closedAt: z
    .date()
    .nullable()
    .transform((value) => (value ? value.toISOString() : null)),
  channel: channelResponseSchema,
  campaignName: z.string().nullable(),
  conversationId: z.bigint().transform(String),
  customerId: z.bigint().transform(String),
  organizationId: z.bigint().transform(String),
  company: z.object({
    id: z.bigint().transform(String),
    name: z.string(),
    logo: z.string().nullable(),
  }),
  assistantMessages: z.number(),
  questionCount: z.number(),
  answeredRate: z.number().nullable(),
  avgLatencySeconds: z.number().nullable(),
  hasTicket: z.boolean(),
  issueCount: z.number(),
  frictionCount: z.number(),
})

export const BackofficeConversationExperimentsResponseDataSchema = z.array(
  BackofficeConversationExperimentResponseDataSchema,
)

export const BackofficeConversationExperimentNamesResponseDataSchema = z.array(z.string())

export const BackofficeConversationExperimentDomainsResponseDataSchema = z.array(z.string())

export const BackofficeConversationExperimentAnalyticsItemResponseDataSchema = z.object({
  experiment: z.string(),
  conversationCount: z.number(),
  avgAssistantMessages: z.number(),
  avgLatencySeconds: z.number().nullable(),
  ticketsRate: z.number(),
  builtInCapabilitiesSuccessRate: z.number(),
  customCapabilitiesSuccessRate: z.number(),
})

export const BackofficeConversationExperimentAnalyticsResponseDataSchema = z.array(
  BackofficeConversationExperimentAnalyticsItemResponseDataSchema,
)

export const BackofficeConversationExperimentKnowledgeAnalyticsItemResponseDataSchema = z.object({
  experiment: z.string(),
  questionCount: z.number(),
  answeredRate: z.number().nullable(),
  knowledgeGapRate: z.number().nullable(),
  interimCoverageRate: z.number().nullable(),
  knowledgeCoverageRate: z.number().nullable(),
  knowledgeBaseCoverageRate: z.number().nullable(),
  knowledgeUsedRate: z.number(),
  goesInCirclesRate: z.number(),
  inventedFactsRate: z.number(),
})

export const BackofficeConversationExperimentKnowledgeAnalyticsResponseDataSchema = z.array(
  BackofficeConversationExperimentKnowledgeAnalyticsItemResponseDataSchema,
)
